import {Storage} from "@ionic/storage";
import {defineStore} from "pinia";
import {AuthStoreState} from "@/types/Stores/AuthStoreState";
import IUser from "@/types/Models/Account/IUser";
import {Mandant} from "@/types/Models/Mandant/Mandant";
import User from "@/models/User";
import {Division} from "@/types/Models/Division/Division";
import {DivisionUser} from "@/types/Models/Division/DivisionUser";
import {MandantUser} from "@/types/Models/Mandant/MandantUser";
import {MandantRolePermission} from "@/types/Models/Permissions/MandantRolePermission";
import {toRaw} from "vue";

export const useAuthStore = defineStore({
    id: "authStore",
    state: () =>
        ({
            user: null,
            mandanten: null,
            currentMandant: null,
            currentDivision: null,
            currentRolePermissions: null,
            /*----- persistent storage -----*/
            storage: new Storage({storeName: "authStore"}),
        }) as AuthStoreState,
    getters: {
        activeUser: (state) => state.user as User | null,
        activeDivision: (state) => state.currentDivision as Division | null,
        activeMandant: (state) => state.currentMandant as Mandant | null,
        activeMandanten: (state) => state.mandanten as Mandant[] | null,
        activeRolePermissions: (state) => state.currentRolePermissions as MandantRolePermission[] | null,
    },
    actions: {
        async init() {
            await this.storage.create();
        },
        async setAccessToken(accessToken: string): Promise<void> {
            await this.storage.set("access", accessToken);
        },
        async getAccessToken(): Promise<string> {
            return await this.storage.get("access");
        },
        async setRefreshToken(refreshToken: string): Promise<void> {
            return await this.storage.set("refresh", refreshToken);
        },
        async getRefreshToken(): Promise<string> {
            return await this.storage.get("refresh");
        },
        async setUser(user: IUser | null): Promise<void> {
            this.user = user !== null ? new User(user) : null;
        },
        async getUser(): Promise<User | null> {
            return this.user;
        },
        async setMandanten(mandanten: Mandant[]) {
            this.mandanten = mandanten;
            const currentMandant = await this.getCurrentMandant();
            if (currentMandant == null) {
                this.currentMandant = mandanten[0];
                await this.storage.set("currentMandant", mandanten[0]);
            } else {
                const mandant = mandanten.find((mandant) => mandant.mandantId == currentMandant.mandantId);
                if (mandant != undefined) {
                    await this.setCurrentMandant(mandant);
                    this.currentDivision =
                        mandant.mandantUser !== undefined && mandant.mandantUser.divisionUser?.division !== undefined
                            ? mandant.mandantUser?.divisionUser?.division
                            : null;
                }
            }
        },
        async switchCurrentMandant(mandantId: number) {
            if (this.mandanten == null) return false;

            const mandant: Mandant | undefined = toRaw(this.mandanten.find((mandant) => mandant.mandantId == mandantId));
            if (mandant == undefined) return false;
            await this.setCurrentMandant(mandant);
            return true;
        },
        async getMandanten(): Promise<Mandant[] | null> {
            return this.mandanten;
        },
        async setCurrentMandant(mandant: Mandant): Promise<void | null> {
            this.currentMandant = mandant;
            await this.setCurrentDivision();
            await this.setCurrentRolePermissions();
            await this.storage.set("currentMandant", mandant);
        },
        async getCurrentMandant(): Promise<Mandant | null> {

            return await this.storage.get("currentMandant");
            //return this.currentMandant;
        },

        async getCurrentMandantUser(): Promise<MandantUser | null> {
            const mandant = await this.getCurrentMandant();
            if (!mandant || !mandant.mandantUser) return null;
            return mandant.mandantUser;
        },

        async getCurrentDivisionUser(): Promise<DivisionUser | null> {
            const mandantUser = await this.getCurrentMandantUser();
            if (!mandantUser || !mandantUser.divisionUser) return null;
            return mandantUser.divisionUser;
        },
        async setCurrentDivision() {
            if (
                this.currentMandant !== null &&
                this.currentMandant.mandantUser &&
                this.currentMandant.mandantUser.divisionUser?.division
            ) {
                this.currentDivision = this.currentMandant.mandantUser.divisionUser?.division;
            } else {
                this.currentDivision = null;
            }
        },
        async getCurrentDivision(): Promise<Division | null> {
            const divisionUser = await this.getCurrentDivisionUser();
            if (!divisionUser || !divisionUser.division) return null;
            return divisionUser.division;
        },
        async setCurrentRolePermissions() {
            const mandantUser = await this.getCurrentMandantUser();

            if (!mandantUser || !mandantUser.userRole || !mandantUser.userRole.mandantRole?.permissions) return null;
            this.currentRolePermissions = mandantUser.userRole.mandantRole?.permissions;

        },
        async getCurrentRolePermissions(): Promise<MandantRolePermission[] | null> {
            const mandantUser = await this.getCurrentMandantUser();
            if (!mandantUser || !mandantUser.userRole || !mandantUser.userRole.mandantRole?.permissions) return null;
            return mandantUser.userRole.mandantRole?.permissions;
        },
        async clearStorage(): Promise<void> {
            await this.storage.clear();
            this.currentDivision = null;
            this.currentMandant = null;
            this.mandanten = null;
            this.user = null;
        },
    },
});
